import React, { useState, useEffect } from 'react';
import { Facebook, MessageCircle, Mail, Phone, Menu, X, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Post from './Post';
import postsAPI from '../services/postsAPI';

// --- 1. The Core Gear Path Generator (REFINED for logo) ---
function generateGearPath(teeth, innerR, outerR, center) {
  const angleStep = (2 * Math.PI) / teeth;
  let path = '';

  for (let i = 0; i < teeth; i++) {
    const startAngle = i * angleStep;
    
    const valleyPoint1Angle = startAngle + angleStep * 0.08; 
    const valleyPoint2Angle = startAngle + angleStep * 0.92;

    const tipPoint1Angle = startAngle + angleStep * 0.25; 
    const tipPoint2Angle = startAngle + angleStep * 0.75;
    
    const p1_valley_start = {
      x: center + innerR * Math.cos(valleyPoint1Angle),
      y: center + innerR * Math.sin(valleyPoint1Angle)
    };
    const p2_tip_start = {
      x: center + outerR * Math.cos(tipPoint1Angle),
      y: center + outerR * Math.sin(tipPoint1Angle)
    };
    const p3_tip_end = {
      x: center + outerR * Math.cos(tipPoint2Angle),
      y: center + outerR * Math.sin(tipPoint2Angle)
    };
    const p4_valley_end = {
      x: center + innerR * Math.cos(valleyPoint2Angle),
      y: center + innerR * Math.sin(valleyPoint2Angle)
    };

    if (i === 0) {
      path += `M ${p1_valley_start.x} ${p1_valley_start.y}`;
    } else {
      path += ` L ${p1_valley_start.x} ${p1_valley_start.y}`;
    }
    
    path += ` L ${p2_tip_start.x} ${p2_tip_start.y}`;
    path += ` L ${p3_tip_end.x} ${p3_tip_end.y}`;
    path += ` L ${p4_valley_end.x} ${p4_valley_end.y}`;
  }

  path += ' Z';
  return path;
}


// --- 2. The new GeoMarkLogo Component ---
function GeoMarkLogo({ width = 16, height = 16 }) {
  const center = 200;
  
  const gearTeeth = 18;
  const gearInnerRadius = 135;
  const gearOuterRadius = 195;

  const gearPathD = generateGearPath(gearTeeth, gearInnerRadius, gearOuterRadius, center);

  return (
    <svg viewBox="0 0 400 400" width={width} height={height}>
      <defs>
        <linearGradient id="goldGradientLogo" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{stopColor:'#FFD700', stopOpacity:1}} />
          <stop offset="100%" style={{stopColor:'#FFA500', stopOpacity:1}} />
        </linearGradient>
      </defs>

      <path
        d={gearPathD}
        fill="url(#goldGradientLogo)"
        stroke="#CC8800"
        strokeWidth="2"
      />
      
      <circle cx="200" cy="200" r={gearInnerRadius - 5} fill="white" />

      <text 
        x="200" y="180" 
        fontFamily="Arial Black, sans-serif" 
        fontSize="56" 
        fontWeight="bold" 
        fill="url(#goldGradientLogo)" 
        textAnchor="middle" 
        letterSpacing="2"
      >
        GEO
      </text>

      <text 
        x="200" y="240" 
        fontFamily="Arial Black, sans-serif" 
        fontSize="56" 
        fontWeight="bold" 
        fill="url(#goldGradientLogo)" 
        textAnchor="middle" 
        letterSpacing="2"
      >
        MARK
      </text>

      <path 
        d="M 125 130 Q 200 85 275 130"
        fill="none"
        stroke="url(#goldGradientLogo)"
        strokeWidth="12"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <path 
        d="M 270 145 L 295 140 L 285 115 Z"
        fill="url(#goldGradientLogo)"
      />

      <path 
        d="M 275 270 Q 200 315 125 270"
        fill="none"
        stroke="url(#goldGradientLogo)"
        strokeWidth="12"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <path 
        d="M 135 252 L 110 255 L 115 285 Z"
        fill="url(#goldGradientLogo)"
      />

    </svg>
  );
}

// --- 3. Translations Object ---
const translations = {
  ka: {
    companyName: "GeoMark",
    tagline: "ინდუსტრიული გადაწყვეტილებები",
    services: "სერვისები",
    posts: "პოსტები",
    contact: "კონტაქტი",
    heroTitle1: "პრემიუმ ლითონის აღჭურვილობა",
    heroTitle2: "თხილის ქარხნებისთვის",
    heroDescription: "ჩვენ ვაწარმოებთ მაღალი ხარისხის ინდუსტრიულ აღჭურვილობას და ლითონის კონსტრუქციებს თხილის დამუშავების ბიზნესებისთვის მთელს საქართველოში.",
    facebook: "ფეისბუქი",
    whatsapp: "ვოტსაპი",
    callUs: "დარეკეთ",
    latestNews: "სიახლეები",
    latestNewsDesc: "იყავით ინფორმირებული ჩვენი უახლესი პროექტების და კომპანიის სიახლეების შესახებ",
    ourServices: "ჩვენი სერვისები",
    servicesDescription: "ინდივიდუალურად შექმნილი ინდუსტრიული გადაწყვეტილებები თხილის დამუშავებისა და სასოფლო-სამეურნეო აღჭურვილობის წარმოებისთვის",
    service1Title: "თხილის დამუშავების აღწერილობა",
    service1Desc: "სპეციალურად შექმნილი მანქანები თხილის დახარისხებისთვის, გაწმენდისა და დამუშავებისთვის",
    service2Title: "ინდუსტრიული ლითონის დამუშავება",
    service2Desc: "მაღალი ხარისხის ლითონის კონსტრუქციები და აღჭურვილობა სასოფლო-სამეურნეო ბიზნესებისთვის",
    service3Title: "ინდივიდუალური წარმოება",
    service3Desc: "მორგებული გადაწყვეტილებები თქვენი თხილის დამუშავების სპეციფიკური საჭიროებებისთვის",
    whyChoose: "რატომ ჯეომარკი?",
    quality: "ხარისხიანი მასალები",
    qualityDesc: "პრემიუმ რკინა და ლითონის მასალები გრძელვადიანი აღჭურვილობისთვის",
    custom: "ინდივიდუალური გადაწყვეტილებები",
    customDesc: "მორგებული დიზაინი თქვენი ბიზნესის სპეციფიკური საჭიროებების დასაკმაყოფილებლად",
    expert: "ექსპერტი გუნდი",
    expertDesc: "გამოცდილი პროფესიონალები ინდუსტრიულ წარმოებაში",
    getInTouch: "დაგვიკავშირდით",
    contactDescription: "მზად ხართ დაიწყოთ თქვენი პროექტი? დაგვიკავშირდით დღესვე კონსულტაციისთვის.",
    footer1: "ყველა უფლება დაცულია",
    footer2: "ინდუსტრიული ლითონის აღჭურვილობის წარმოება"
  },
  en: {
    companyName: "GeoMark",
    tagline: "Industrial Solutions",
    services: "Services",
    posts: "Posts",
    contact: "Contact",
    heroTitle1: "Premium Metal Equipment for",
    heroTitle2: "Hazelnut Processing",
    heroDescription: "We manufacture high-quality industrial equipment and metal structures for hazelnut processing businesses across Georgia.",
    facebook: "Facebook",
    whatsapp: "WhatsApp",
    callUs: "Call Us",
    latestNews: "Latest News & Updates",
    latestNewsDesc: "Stay updated with our latest projects and company news",
    ourServices: "Our Services",
    servicesDescription: "Custom-built industrial solutions for hazelnut processing and agricultural equipment manufacturing",
    service1Title: "Hazelnut Processing Equipment",
    service1Desc: "Custom-built machinery for sorting, cleaning, and processing hazelnuts",
    service2Title: "Industrial Metal Fabrication",
    service2Desc: "High-quality metal structures and equipment for agricultural businesses",
    service3Title: "Custom Manufacturing",
    service3Desc: "Tailored solutions for your specific hazelnut processing needs",
    whyChoose: "Why Choose GeoMark?",
    quality: "Quality Materials",
    qualityDesc: "Premium iron and metal materials for long-lasting equipment",
    custom: "Custom Solutions",
    customDesc: "Tailored designs to meet your specific business needs",
    expert: "Expert Team",
    expertDesc: "Experienced professionals in industrial manufacturing",
    getInTouch: "Get In Touch",
    contactDescription: "Ready to start your project? Contact us today for a consultation.",
    footer1: "All rights reserved",
    footer2: "Industrial Metal Equipment Manufacturing"
  },
  ru: {
    companyName: "GeoMark",
    tagline: "Промышленные решения",
    services: "Услуги",
    posts: "Посты",
    contact: "Контакты",
    heroTitle1: "Премиальное металлическое оборудование",
    heroTitle2: "для переработки фундука",
    heroDescription: "Мы производим высококачественное промышленное оборудование и металлоконструкции для предприятий по переработке фундука по всей Грузии.",
    facebook: "Фейсбук",
    whatsapp: "Ватсап",
    callUs: "Позвоните",
    latestNews: "Последние новости и обновления",
    latestNewsDesc: "Будьте в курсе наших последних проектов и новостей компании",
    ourServices: "Наши услуги",
    servicesDescription: "Индивидуально созданные промышленные решения для переработки фундука и производства сельскохозяйственного оборудования",
    service1Title: "Оборудование для переработки фундука",
    service1Desc: "Специально созданные машины для сортировки, очистки и переработки фундука",
    service2Title: "Промышленная обработка металла",
    service2Desc: "Высококачественные металлоконструкции и оборудование для сельскохозяйственного бизнеса",
    service3Title: "Индивидуальное производство",
    service3Desc: "Индивидуальные решения для ваших конкретных потребностей в переработке фундука",
    whyChoose: "Почему выбирают ГеоМарк?",
    quality: "Качественные материалы",
    qualityDesc: "Премиальное железо и металлические материалы для долговечного оборудования",
    custom: "Индивидуальные решения",
    customDesc: "Индивидуальный дизайн для удовлетворения ваших конкретных бизнес-потребностей",
    expert: "Экспертная команда",
    expertDesc: "Опытные специалисты в промышленном производстве",
    getInTouch: "Свяжитесь с нами",
    contactDescription: "Готовы начать свой проект? Свяжитесь с нами сегодня для консультации.",
    footer1: "Все права защищены",
    footer2: "Производство промышленного металлического оборудования"
  }
};

export default function GeoMarkWebsite({ language: propLanguage, setLanguage: propSetLanguage }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [language, setLanguageState] = useState(propLanguage || 'ka');
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const navigate = useNavigate();

  // Use prop setLanguage if provided, otherwise use local state
  const setLanguage = propSetLanguage || setLanguageState;

  // Update local language when prop changes
  useEffect(() => {
    if (propLanguage) {
      setLanguageState(propLanguage);
    }
  }, [propLanguage]);

  const t = translations[language]; // Current translations

  // Load posts from API or localStorage
  useEffect(() => {
    const loadPosts = async () => {
      try {
        const fetchedPosts = await postsAPI.getPosts();
        setPosts(fetchedPosts);
      } catch (error) {
        console.error('Error loading posts:', error);
      }
    };
    loadPosts();
  }, []);

  const facebookUrl = "https://www.facebook.com/your-page";
  const whatsappNumber = "995123456789";
  const phoneNumber = "+995 123 456 789";
  const email = "info@geomark.ge";

  const services = [
    {
      title: t.service1Title,
      description: t.service1Desc,
      image: "https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=800",
      link: "/service/hazelnut-processing"
    },
    {
      title: t.service2Title,
      description: t.service2Desc,
      image: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=800",
      link: "/service/metal-fabrication"
    },
    {
      title: t.service3Title,
      description: t.service3Desc,
      image: "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=800",
      link: "/service/custom-manufacturing"
    }
  ];

  const languages = [
    { code: 'ka', name: 'ქართული', flag: '🇬🇪' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="w-16 h-16 relative">
                <GeoMarkLogo width="100%" height="100%" /> 
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">{t.companyName}</h1>
                <p className="text-sm text-slate-600">{t.tagline}</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              <a href="#services" className="text-slate-700 hover:text-orange-500 transition">{t.services}</a>
              <a href="#posts" className="text-slate-700 hover:text-orange-500 transition">{t.posts}</a>
              <a href="#contact" className="text-slate-700 hover:text-orange-500 transition">{t.contact}</a>
              
              {/* Language Switcher - Desktop */}
              <div className="relative">
                <button
                  onClick={() => setLangMenuOpen(!langMenuOpen)}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition"
                >
                  <Globe size={20} />
                  <span className="text-sm font-medium">{languages.find(l => l.code === language)?.flag}</span>
                </button>
                
                {langMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-slate-200 py-2">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setLanguage(lang.code);
                          setLangMenuOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 hover:bg-slate-100 transition flex items-center space-x-3 ${
                          language === lang.code ? 'bg-orange-50 text-orange-600' : 'text-slate-700'
                        }`}
                      >
                        <span className="text-xl">{lang.flag}</span>
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <nav className="md:hidden py-4 border-t">
              <a href="#services" className="block py-2 text-slate-700" onClick={() => setMobileMenuOpen(false)}>{t.services}</a>
              <a href="#posts" className="block py-2 text-slate-700" onClick={() => setMobileMenuOpen(false)}>{t.posts}</a>
              <a href="#contact" className="block py-2 text-slate-700" onClick={() => setMobileMenuOpen(false)}>{t.contact}</a>
              
              {/* Language Switcher - Mobile */}
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-slate-500 mb-2 px-2">Language / Язык / ენა</p>
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full text-left px-2 py-2 rounded flex items-center space-x-3 ${
                      language === lang.code ? 'bg-orange-50 text-orange-600' : 'text-slate-700'
                    }`}
                  >
                    <span className="text-xl">{lang.flag}</span>
                    <span>{lang.name}</span>
                  </button>
                ))}
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-slate-800 mb-6">
            {t.heroTitle1}
            <span className="block text-orange-500 mt-2">{t.heroTitle2}</span>
          </h2>
          <p className="text-xl text-slate-600 mb-12 max-w-3xl mx-auto">
            {t.heroDescription}
          </p>
          
          {/* Social Links */}
          <div className="flex justify-center items-center space-x-4 mb-8">
            <a 
              href={facebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition shadow-lg"
            >
              <Facebook size={20} />
              <span>{t.facebook}</span>
            </a>
            <a 
              href={`https://wa.me/${whatsappNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-green-500 text-white px-6 py-3 rounded-lg hover:bg-green-600 transition shadow-lg"
            >
              <MessageCircle size={20} />
              <span>{t.whatsapp}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Posts Section */}
      {posts.length > 0 && (
        <section id="posts" className="py-20 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <h3 className="text-4xl font-bold text-center text-slate-800 mb-4">
              {t.latestNews}
            </h3>
            <p className="text-center text-slate-600 mb-16 max-w-2xl mx-auto">
              {t.latestNewsDesc}
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              {posts.slice(0, 9).map(post => (
                <Post key={post.id} post={post} language={language} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services Section */}
      <section id="services" className="py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-4xl font-bold text-center text-slate-800 mb-4">
            {t.ourServices}
          </h3>
          <p className="text-center text-slate-600 mb-16 max-w-2xl mx-auto">
            {t.servicesDescription}
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="group cursor-pointer"
                onClick={() => navigate(service.link)}
              >
                <div className="overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition duration-300">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-64 object-cover group-hover:scale-110 transition duration-500"
                  />
                  <div className="p-6 bg-white">
                    <h4 className="text-xl font-bold text-slate-800 mb-3">{service.title}</h4>
                    <p className="text-slate-600 mb-4">{service.description}</p>
                    <span className="text-orange-500 font-medium group-hover:underline">
                      {language === 'ka' ? 'ვრცლად →' : language === 'ru' ? 'Подробнее →' : 'Learn More →'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-orange-50 to-yellow-50">
        <div className="max-w-5xl mx-auto text-center">
          <h3 className="text-4xl font-bold text-slate-800 mb-6">
            {t.whyChoose}
          </h3>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl text-white font-bold">1</span>
              </div>
              <h4 className="text-xl font-bold text-slate-800 mb-3">{t.quality}</h4>
              <p className="text-slate-600">{t.qualityDesc}</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl text-white font-bold">2</span>
              </div>
              <h4 className="text-xl font-bold text-slate-800 mb-3">{t.custom}</h4>
              <p className="text-slate-600">{t.customDesc}</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl text-white font-bold">3</span>
              </div>
              <h4 className="text-xl font-bold text-slate-800 mb-3">{t.expert}</h4>
              <p className="text-slate-600">{t.expertDesc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-slate-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-4xl font-bold mb-6">{t.getInTouch}</h3>
          <p className="text-xl text-slate-300 mb-12">
            {t.contactDescription}
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            <a 
              href={`tel:${phoneNumber.replace(/\s/g, '')}`}
              className="flex items-center justify-center space-x-3 bg-slate-700 p-6 rounded-lg hover:bg-slate-600 transition"
            >
              <Phone size={24} />
              <span className="text-lg">{phoneNumber}</span>
            </a>
            <a 
              href={`mailto:${email}`}
              className="flex items-center justify-center space-x-3 bg-slate-700 p-6 rounded-lg hover:bg-slate-600 transition"
            >
              <Mail size={24} />
              <span className="text-lg">{email}</span>
            </a>
          </div>

          <div className="mt-12 flex justify-center space-x-6">
            <a 
              href={facebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 p-4 rounded-full hover:bg-blue-700 transition shadow-lg"
            >
              <Facebook size={24} />
            </a>
            <a 
              href={`https://wa.me/${whatsappNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 p-4 rounded-full hover:bg-green-600 transition shadow-lg"
            >
              <MessageCircle size={24} />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p>&copy; 2026 GeoMark.ge - {t.footer1}</p>
          <p className="mt-2 text-sm">{t.footer2}</p>
        </div>
      </footer>
    </div>
  );
}